import { format, isSameDay, isToday, startOfMonth } from "date-fns";
import { ArrowLeftIcon } from "lucide-react";
import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import { Card } from "@/components/card";
import { DayCalendar } from "@/components/day-calendar";
import { LoadingScreen } from "@/components/loading-screen";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { useGetRoomSystem } from "@/features/room-systems/api/get-room-system";
import { SessionRoomSystemForm } from "@/features/room-systems/components/session-room-system.form";
import { useFetchSessionsByRoomSystem } from "@/features/session/api/fetch-sessions-by-room-system";
import { keepPreviousData } from "@tanstack/react-query";

export function SessionRoomSystem() {
  const { id } = useParams<{ id: string }>();
  const [date, setDate] = useState<Date>(new Date());
  const [month, setMonth] = useState<Date>(new Date());
  const { data, isLoading, isError, isSuccess } = useGetRoomSystem({
    id: id ?? "",
  });
  const {
    data: sessions,
    isLoading: isSessionsLoading,
    isFetching: isSessionsFetching,
  } = useFetchSessionsByRoomSystem(
    id ?? "",
    format(startOfMonth(month), "yyyy-MM-dd"),
    {
      placeholderData: keepPreviousData,
    },
  );

  if (isLoading) {
    return (
      <div>
        <div className="mb-6">
          <Button variant="link" asChild>
            <Link to="/sessions">
              <ArrowLeftIcon className="mr-2 h-4 w-4" /> Back to session
            </Link>
          </Button>
        </div>
        <OnboardingHeader title="Room System Details" className="mb-6" />

        <LoadingScreen />
      </div>
    );
  }

  if (isError || !isSuccess) {
    return <Navigate to="/sessions/room-systems" />;
  }

  const isDateToday = isToday(date);

  const sessionsToSelectedDate =
    sessions?.filter((session) => isSameDay(session.start, date)) || [];

  const listOfDaysWhenSessions =
    sessions?.map((session) => new Date(session.start)) || [];

  return (
    <section>
      <div className="mb-6">
        <Button variant="link" asChild>
          <Link to="/sessions/room-systems">
            <ArrowLeftIcon className="mr-2 h-4 w-4" /> Back to session
          </Link>
        </Button>
      </div>
      <OnboardingHeader title={data.displayName} className="mb-6" />
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-7">
          <Card>
            <Card.Content className="flex justify-center">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(date) => setDate(date ? new Date(date) : new Date())}
                activeDays={listOfDaysWhenSessions}
                onMonthChange={(date) => {
                  setMonth(date);
                }}
              />
            </Card.Content>
          </Card>

          <div className="mt-4">
            {isSessionsLoading || isSessionsFetching ? (
              <LoadingScreen />
            ) : (
              <DayCalendar
                sessions={sessionsToSelectedDate}
                isToday={isDateToday}
              />
            )}
          </div>
        </div>
        <div className="col-span-12 md:col-span-5">
          <SessionRoomSystemForm initialData={data} />
        </div>
      </div>
    </section>
  );
}
